import { round } from '@oliasoft-open-source/units';
import ExcelJS from 'exceljs';
import type { Row, Cell, Worksheet, BorderStyle, Border } from 'exceljs';
import type { ICostResultsItem, ISimulationResult } from 'src/common/interfaces/simulations.interfaces';
import { writeExcelFile } from 'src/common/write-excel-file/write-excel-file';
import i18n from 'src/internationalisation/i18n';
import translations from 'src/internationalisation/translation-map.json';
import { autoWidth } from 'src/views/time-tracker/utils/time-tracker-download';
import { HOURS_PER_DAY } from 'src/enums/general';
import { thousandsSeparators } from 'src/views/cost-tracker/cost-tracker-common/utils/utils';
import { toast } from '@oliasoft-open-source/react-ui-library';

const headerTranslationsMap: { [key: string]: string } = {
  code: i18n.t(translations.code),
  cost: i18n.t(translations.simulations_costCategoryItem),
  total: i18n.t(translations.total),
};
const headers = ['code', 'cost', 'total'];

const borderStyle: Border = {
  style: 'thin',
  color: { argb: '282828' },
};

const applyBorder = (cell: Cell, borders: Partial<Cell['border']>) => {
  cell.border = { ...cell.border, ...borders };
};

const applyStyle = (cell: Cell, style: Partial<Cell['style']>) => {
  cell.style = { ...cell.style, ...style };
};

const createOuterBorder = (
  worksheet: Worksheet,
  [startCol, startRow]: [number, number],
  [endCol, endRow]: [number, number],
  style: BorderStyle = 'thin',
) => {
  for (let row = startRow; row <= endRow; row++) {
    for (let col = startCol; col <= endCol; col++) {
      const cell = worksheet.getCell(row, col);
      if (col <= 3 || col === endCol) {
        applyBorder(cell, { right: { style } });
      }
    }
  }
};

const colorHeaderRow = (row: Row) => {
  row.eachCell((cell: Cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FF818162' },
    };
    cell.font = {
      bold: true,
      color: { argb: 'FFE0E0DA' },
      family: 2,
      name: 'Calibri',
      size: 11,
    };
    cell.alignment = { horizontal: 'center', vertical: 'middle' };
    cell.border = {
      top: borderStyle,
      bottom: borderStyle,
    };
  });
};

const colorCategoryRow = (row: Row) => {
  row.eachCell((cell: Cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'E2E2DC' },
    };
  });
};

export const downloadAFEReportExcel = (
  simulationsResult: ISimulationResult,
  costCodesIdToCodeMap: any,
  primaryCurrency: string,
  projectName: string,
  wellboreName: string,
) => {
  const {
    cost_results: costResults = [],
    section_cost_per_item: sectionCostPerItem = {},
    section_contribution_to_cost_item: sectionContributionToCost = {},
    section_costs: sectionCosts = { labels: [], costs: { mean: [] } },
    section_durations: sectionDurations = { labels: [], durations: { mean: [] } },
  } = simulationsResult;
  const workBook = new ExcelJS.Workbook();
  const workSheet = workBook.addWorksheet(i18n.t(translations.projects_afeReport));

  workSheet.properties.outlineLevelRow = 1;

  const sectionList = Object.keys(sectionCostPerItem).filter((key) => sectionCostPerItem[key].length > 0);
  const startRow = workSheet.rowCount;
  workSheet.addRow([...headers, ...sectionList].map((header) => headerTranslationsMap?.[header] || header));
  if (workSheet.lastRow) {
    colorHeaderRow(workSheet.lastRow);
  }

  const sectionsMeanDaysDuration = sectionList.map((sectionName) => {
    const sectionIdx = sectionDurations.labels.findIndex((section) => section === sectionName);
    return `${round(sectionDurations.durations.mean[sectionIdx] / HOURS_PER_DAY, 1)} ${i18n.t(translations.days)}`;
  });

  workSheet
    .addRow([
      '',
      '',
      `${round(simulationsResult.average / HOURS_PER_DAY, 1)} ${i18n.t(translations.days)}`,
      ...sectionsMeanDaysDuration,
    ])
    .eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'E2E2DC' },
      };
      cell.alignment = { horizontal: 'center' };
      cell.font = {
        bold: true,
        family: 2,
      };
      applyBorder(cell, { top: { style: 'thin' as BorderStyle } });
    });

  workSheet.addRow(['', '', primaryCurrency, ...sectionList.map((_) => primaryCurrency)]).eachCell((cell) => {
    cell.alignment = { horizontal: 'center' };
    applyBorder(cell, {
      top: { style: 'thin' as BorderStyle },
      bottom: { style: 'thin' as BorderStyle },
    });
  });
  const generateSectionCost = (costId: string) => {
    const contributedSections = sectionContributionToCost[costId];
    return sectionList.map((sectionName) => {
      const roundedCost = round(contributedSections?.find((section) => section.name === sectionName)?.mean, 0);
      return roundedCost ? thousandsSeparators(roundedCost) : '';
    });
  };

  const collectCategoryCostIds = (tree: ICostResultsItem[]): string[] => {
    return tree.reduce<string[]>(
      (acc, node) => acc.concat(node.id, node.children ? collectCategoryCostIds(node.children) : []),
      [],
    );
  };

  const createRows = (costResults: ICostResultsItem[], depth = 0) => {
    costResults?.forEach((costResult) => {
      const row = workSheet.addRow([
        costCodesIdToCodeMap.get(costResult?.cost_code_id),
        costResult.name,
        thousandsSeparators(round(costResult.results[0], 0)),
        ...generateSectionCost(costResult.id),
      ]);

      row.eachCell((cell) => applyStyle(cell, { alignment: { horizontal: 'center' } }));
      row.getCell(2).alignment = { indent: depth };
      row.outlineLevel = depth;

      if (costResult.children && costResult.children.length > 0) {
        row.eachCell((cell) => applyStyle(cell, { font: { bold: true } }));
        if (depth === 0 && workSheet.lastRow) {
          colorCategoryRow(workSheet.lastRow);

          const categoryCostIds = collectCategoryCostIds(costResult.children);

          sectionList?.forEach((sectionName, idx) => {
            const totalCost = sectionCostPerItem[sectionName].reduce((sum, cost) => {
              if (categoryCostIds.includes(cost.id)) {
                return sum + cost.mean;
              } else {
                return sum;
              }
            }, 0);
            row.getCell(3 + idx + 1).value = thousandsSeparators(round(totalCost, 0));
          });
        }
        createRows(costResult.children, depth + 1);

        costResult?.children?.forEach((_, index) => {
          const childRow = workSheet.getRow(row.number + index + 1);
          childRow.hidden = true;
        });
      }
    });
  };
  createRows(costResults);
  const sectionsTotal = sectionList.map((sectionName) => {
    const sectionIdx = sectionCosts.labels.findIndex((section) => section === sectionName);
    const roundedCost = round(sectionCosts.costs.mean[sectionIdx], 0);
    return thousandsSeparators(roundedCost);
  });
  workSheet
    .addRow([
      '',
      `${i18n.t(translations.total)}`,
      thousandsSeparators(round(simulationsResult.average_cost, 0)),
      ...sectionsTotal,
    ])
    .eachCell((cell) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'B4B4A0' },
      };
      cell.alignment = { horizontal: 'center' };
      applyBorder(cell, { top: { style: 'thin' as BorderStyle } });
    });

  const endRow = workSheet.rowCount;
  const startCol = 1;
  const endCol = 3 + sectionList.length;
  createOuterBorder(workSheet, [startCol, startRow], [endCol, endRow]);

  autoWidth(workSheet, 10);

  writeExcelFile(workBook, `${i18n.t(translations.projects_afeReport)}_${projectName}_${wellboreName}`);
  toast({
    message: {
      type: 'Success',
      content: i18n.t(translations.projects_afeReportExcelMessage) as string,
    },
  });
};
